import type { BrowserTestContext } from "@/dev/BrowserTestContext.ts";
import type { Matcher, waitForOptions } from "@testing-library/dom";
import invariant from "tiny-invariant";

export async function waitNotDisabled(
    c: BrowserTestContext,
    element: HTMLElement & { disabled: boolean },
    waitForOptions?: waitForOptions,
) {
    // Shortcut: Prevent logging the wait in the test console (for conciseness)
    if (!element.disabled) {
        return;
    }
    await c.dom.waitFor(
        function elementNotDisabled() {
            if (element.disabled) {
                throw new Error(`<${element.tagName}> is disabled or loading`);
            }
        },
        { timeout: 5000, ...waitForOptions },
    );
}

export async function fill(c: BrowserTestContext, label: Matcher, value: string) {
    await c.step(`Fill the "${label}" input`, async () => {
        const input = await c.canvas.findByLabelText(label);
        invariant(input instanceof HTMLInputElement, `Expected ${label} to be an input element`);
        if (input.value !== "") {
            await c.userEvent.clear(input);
        }
        // await expect(input).toBeVisible();
        // await expect(input).toHaveFocus();
        await c.userEvent.type(input, value);
    });
}

// XXX: Copy-pasta of above, delete
export async function fillModal(c: BrowserTestContext, label: Matcher, value: string) {
    await c.step(`Fill the "${label}" modal input`, async () => {
        const input = await c.canvas.findByLabelText(label);
        invariant(input instanceof HTMLInputElement, `Expected ${label} to be an input element`);
        if (input.value !== "") {
            await c.userEvent.clear(input);
        }
        // await expect(input).toBeVisible();
        // await expect(input).toHaveFocus();
        await c.userEvent.type(input, value);
    });
}

export async function clickButton(c: BrowserTestContext, name: string | RegExp) {
    await c.step(`Click the "${name}" button`, async () => {
        const button = await c.canvas.findByRole<HTMLButtonElement>("button", { name });
        await waitNotDisabled(c, button);
        await c.userEvent.click(button);
    });
}

// XXX: Copy-pasta of above, should split "within" and click responsibilities
export async function clickModalButton(c: BrowserTestContext, modalName: string | RegExp, name: string | RegExp) {
    await c.step(`Validate modal and click the "${name}" button`, async () => {
        const modalDialog = await c.canvas.findByRole("dialog", { name: modalName });
        const modal = c.dom.within(modalDialog);

        const button = await modal.findByRole<HTMLButtonElement>("button", { name });
        await waitNotDisabled(c, button);
        await c.userEvent.click(button);
    });
}

// XXX: Should split "within" and click responsibilities
export async function clickDeleteLastRow(c: BrowserTestContext, label: string | RegExp) {
    await c.step(`Click the last row "${label}" button`, async () => {
        const rows = c.canvas.getAllByRole("row");
        const lastRow = rows[rows.length - 1];

        const button = c.dom.within(lastRow).getByLabelText(label);
        await c.userEvent.click(button);
    });
}

export async function clickSelect(c: BrowserTestContext) {
    // XXX: Selector too general
    const button = await c.canvas.findByText("Please select");
    await c.userEvent.click(button);
}

export async function selectRadio(c: BrowserTestContext, name: string | RegExp) {
    await c.step(`Select the "${name}" radio option`, async () => {
        const radio = await c.canvas.findByRole<HTMLInputElement>("radio", {
            name,
        });

        await waitNotDisabled(c, radio);
        await c.userEvent.click(radio);
    });
}

// XXX: Pointless wrapper function, inline
export async function exists(c: BrowserTestContext, name: string | RegExp, selector: string) {
    return await c.canvas.findByText(name, { selector: selector }, { timeout: 3000 });
}
